<template>
    <div class="exception-range-planning md:ppv2-p-5 ppv2-p-3">
        <!-- Error Messages -->
        <div v-if="Object.keys(errors).length > 0" class="ppv2-space-y-2 ppv2-mb-4">
            <InfoMessageBox v-for="(error, key) in errors" :key="key" :message="error" color="danger" />
        </div>

        <div class="ppv2-flex ppv2-flex-col ppv2-gap-10">
            <div class="ppv2-flex sm:ppv2-flex-row ppv2-flex-col ppv2-gap-2 ppv2-justify-between ppv2-items-center ppv2-mb-6">
                <div class="ppv2-flex ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer"
                        :class="{ 'ppv2-bg-[#36A3D2] ppv2-text-white': !isTimeSlot, 'ppv2-border-gray-300': isTimeSlot }"
                        @click="handleTimeSlot(false)"
                    >
                        <div class="ppv2-w-5 ppv2-h-5 ppv2-flex ppv2-items-center ppv2-justify-center">
                            <div v-if="!isTimeSlot" class="ppv2-w-3 ppv2-h-3 ppv2-bg-white ppv2-rounded-sm"></div>
                        </div>
                        <span>{{ $t('resource_planning.scheduling_temporary_closures.all_day_title') }}</span>
                    </div>
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer"
                        :class="{ 'ppv2-bg-[#36A3D2] ppv2-text-white': isTimeSlot, 'ppv2-border-gray-300': !isTimeSlot }"
                        @click="handleTimeSlot(true)"
                    >
                        <div class="ppv2-w-5 ppv2-h-5 ppv2-flex ppv2-items-center ppv2-justify-center">
                            <div v-if="isTimeSlot" class="ppv2-w-3 ppv2-h-3 ppv2-bg-white ppv2-rounded-sm"></div>
                        </div>
                        <span>{{ $t('resource_planning.scheduling_temporary_closures.time_slot_title') }}</span>
                    </div>
                </div>
            </div>

            <!-- Title Input -->
            <CustomInput
                v-model="exceptionData.title"
                :label="$t('resource_planning.scheduling_temporary_closures.close_label')"
                :placeholder="$t('resource_planning.scheduling_temporary_closures.close_placeholder')"
                required
                @input="handleTitleInput"
                :customSizeLabel="14"
            />

            <!-- Color Picker -->
            <div class="ppv2-flex ppv2-flex-col ppv2-gap-2">
                <label class="ppv2-text-sm poppinsSemiBold ppv2-text-black">{{
                    $t('resource_planning.scheduling_temporary_closures.color_label')
                }}</label>
                <div class="ppv2-relative">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-px-4 ppv2-py-3 ppv2-border ppv2-border-gray-300 ppv2-rounded-xl ppv2-cursor-pointer"
                        @click="showColorPicker = !showColorPicker"
                    >
                        <div
                            class="ppv2-w-6 ppv2-h-6 ppv2-rounded-full ppv2-mr-2"
                            :style="{ backgroundColor: selectedColor.hex }"
                        ></div>
                        <span class="ppv2-text-gray-400">{{ $t(selectedColor.name) }}</span>
                        <div class="ppv2-ml-auto ppv2-w-3 ppv2-h-1.5 ppv2-relative">
                            <div class="ppv2-absolute ppv2-w-full ppv2-h-0.5 ppv2-bg-gray-400 ppv2-rotate-45"></div>
                            <div class="ppv2-absolute ppv2-w-full ppv2-h-0.5 ppv2-bg-gray-400 ppv2--rotate-45"></div>
                        </div>
                    </div>

                    <!-- Color Picker Dropdown -->
                    <div
                        v-if="showColorPicker"
                        class="ppv2-absolute ppv2-z-10 ppv2-mt-1 ppv2-w-full ppv2-bg-white ppv2-shadow-lg ppv2-rounded-lg ppv2-p-4"
                    >
                        <div class="ppv2-grid ppv2-grid-cols-5 ppv2-gap-2">
                            <div
                                v-for="color in colorOptions"
                                :key="color.hex"
                                class="ppv2-w-8 ppv2-h-8 ppv2-rounded-full ppv2-cursor-pointer ppv2-flex ppv2-items-center ppv2-justify-center"
                                :style="{ backgroundColor: color.hex }"
                                @click="selectColor(color)"
                            >
                                <div
                                    v-if="selectedColor.hex === color.hex"
                                    class="ppv2-w-4 ppv2-h-4 ppv2-bg-white ppv2-rounded-full"
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Date Range -->
            <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2" :style="{ gap: normalizeSize(16, true) }">
                <div>
                    <BaseDateInput
                        v-model="exceptionData.start"
                        :min-date="minDate"
                        :max-date="exceptionData.end"
                        :default-value="new Date()"
                        required
                        @input="() => handleDateInput('start')"
                        :label="$t('resource_planning.weekRangeScheduling.date_begin_label')"
                        :customSizeLabel="14"
                    />
                </div>
                <div>
                    <BaseTimeInput
                        v-model="exceptionData.startTime"
                        :disabled="!exceptionData.start || !isTimeSlot"
                        required
                        @input="handleTimeInput"
                        :label="$t('resource_planning.scheduling_temporary_closures.hour_begin_label')"
                        :customSizeLabel="14"
                    />
                </div>
            </div>

            <!-- Time Range (Conditional) -->
            <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2" :style="{ gap: normalizeSize(16, true) }">
                <div>
                    <BaseDateInput
                        v-model="exceptionData.end"
                        :min-date="exceptionData.start || minDate"
                        :default-value="new Date()"
                        required
                        @input="() => handleDateInput('end')"
                        :label="$t('resource_planning.weekRangeScheduling.date_end_label')"
                        :customSizeLabel="14"
                    />
                </div>
                <div>
                    <BaseTimeInput
                        v-model="exceptionData.endTime"
                        :min-time="isSameDate ? exceptionData.startTime : null"
                        :disabled="!exceptionData.end || !isTimeSlot"
                        required
                        @input="handleTimeInput"
                        :label="$t('resource_planning.scheduling_temporary_closures.hour_end_label')"
                        :customSizeLabel="14"
                    />
                </div>
            </div>

            <!-- Reason Selection -->
            <div class="ppv2-flex ppv2-flex-col ppv2-gap-2">
                <label class="ppv2-text-sm poppinsSemiBold ppv2-text-black">
                    {{ $t('resource_planning.scheduling_temporary_closures.reason_label') }}
                    <span class="text-purple">*</span>
                </label>

                <div class="ppv2-relative">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-px-4 ppv2-py-3 ppv2-border ppv2-border-gray-300 ppv2-rounded-xl ppv2-cursor-pointer"
                        @click="showReasonDropdown = !showReasonDropdown"
                    >
                        <span class="ppv2-text-gray-400">{{
                            exceptionData?.reason?.title ||
                            $t('resource_planning.scheduling_temporary_closures.reason_placeholder')
                        }}</span>
                        <div class="ppv2-ml-auto ppv2-w-3 ppv2-h-1.5 ppv2-relative">
                            <div class="ppv2-absolute ppv2-w-full ppv2-h-0.5 ppv2-bg-gray-400 ppv2-rotate-45"></div>
                            <div class="ppv2-absolute ppv2-w-full ppv2-h-0.5 ppv2-bg-gray-400 ppv2--rotate-45"></div>
                        </div>
                    </div>

                    <!-- Reason Dropdown -->
                    <div
                        v-if="showReasonDropdown"
                        class="ppv2-absolute ppv2-z-10 ppv2-mt-1 ppv2-w-full ppv2-bg-white ppv2-shadow-lg ppv2-rounded-lg ppv2-p-2 ppv2-max-h-60 ppv2-overflow-auto"
                    >
                        <div
                            v-for="(reason, index) in translatedReasonOptions"
                            :key="index"
                            class="ppv2-px-4 ppv2-py-2 ppv2-rounded-lg ppv2-cursor-pointer hover:ppv2-bg-gray-100"
                            @click="() => selectReason(reason)"
                        >
                            {{ reason.title }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Save Button -->
            <div class="ppv2-flex ppv2-justify-end ppv2-mt-8">
                <BaseButton
                    variant="primary"
                    @click="saveException"
                    :customSizeLabel="14"
                    :disabled="Object.keys(errors).length > 0"
                >
                    {{ $t('common.save') }}
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../../utils/normalizeSize'

import BaseDateInput from '../../Shared/BaseDateInput.vue'
import BaseTimeInput from '../../Shared/BaseTimeInput.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomInput from '../../Inputs/CustomInput.vue'
import BaseLabel from '../../Shared/BaseLabel.vue'
import InfoMessageBox from '../../Shared/InfoMessageBox.vue'

// Status mapping for exceptions
const EXCEPTION_STATUS = {
    0: { title: 'Travaux', color: '#FF6B35' },
    1: { title: 'Fermeture exceptionnelle', color: '#36A3D2' },
    2: { title: 'Fermeture annuelle', color: '#4CAF50' },
    3: { title: 'Grève', color: '#F44336' },
    4: { title: 'Autre', color: '#9C27B0' },
}

// Color options for the color picker
const COLOR_OPTIONS = [
    { name: 'resource_planning.scheduling_temporary_closures.closure_color.red', hex: '#F44336' },
    { name: 'resource_planning.scheduling_temporary_closures.closure_color.blue', hex: '#2196F3' },
    { name: 'resource_planning.scheduling_temporary_closures.closure_color.green', hex: '#4CAF50' },
    { name: 'resource_planning.scheduling_temporary_closures.closure_color.orange', hex: '#FF9800' },
    { name: 'resource_planning.scheduling_temporary_closures.closure_color.purple', hex: '#9C27B0' },
    { name: 'resource_planning.scheduling_temporary_closures.closure_color.pink', hex: '#E91E63' },
    { name: 'resource_planning.scheduling_temporary_closures.closure_color.cyan', hex: '#00BCD4' },
    { name: 'resource_planning.scheduling_temporary_closures.closure_color.yellow', hex: '#FFEB3B' },
    { name: 'resource_planning.scheduling_temporary_closures.closure_color.gray', hex: '#9E9E9E' },
    { name: 'resource_planning.scheduling_temporary_closures.closure_color.dark_blue', hex: '#1976D2' },
]

export default {
    name: 'ExceptionRangePlanning',
    components: {
        BaseDateInput,
        BaseTimeInput,
        CustomInput,
        BaseLabel,
        InfoMessageBox,
        BaseButton,
    },
    props: {
        mode: {
            type: String,
            default: 'add',
            validator: (value) => ['add', 'edit'].includes(value),
        },
        selectedException: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            isTimeSlot: true,
            showColorPicker: false,
            errors: {},
            minDate: new Date(), // Today's date as minimum date
            showReasonDropdown: false,
            exceptionData: {
                title: '',
                establishment_id: 1,
                status: 1,
                start: '',
                end: '',
                startTime: '09:00',
                endTime: '18:00',
                color: '#36A3D2',
                reason: null,
            },
            statusOptions: EXCEPTION_STATUS,
            colorOptions: COLOR_OPTIONS,
            selectedColor: { name: 'resource_planning.scheduling_temporary_closures.closure_color.blue', hex: '#36A3D2' },
            reasonOptions: [
                { id: 0, titleKey: 'resource_planning.reasons.works' },
                { id: 1, titleKey: 'resource_planning.reasons.exceptional_closure' },
                { id: 2, titleKey: 'resource_planning.reasons.breakdown' },
                { id: 3, titleKey: 'resource_planning.reasons.maintenance' },
                { id: 4, titleKey: 'resource_planning.reasons.team_meeting' },
                { id: 5, titleKey: 'resource_planning.reasons.private_event' },
            ],
        }
    },
    created() {
        if (this.mode === 'edit' && this.selectedException) {
            this.initializeWithSelectedException()
        }
    },
    computed: {
        isSameDate() {
            if (!this.exceptionData.start || !this.exceptionData.end) return false
            return this.exceptionData.start === this.exceptionData.end
        },
        formattedStartDate() {
            return this.formatDisplayDate(this.exceptionData.start)
        },
        formattedEndDate() {
            return this.formatDisplayDate(this.exceptionData.end)
        },
        translatedReasonOptions() {
            return this.reasonOptions.map((r) => ({
                ...r,
                title: this.$t(r.titleKey), // traduction dynamique
            }))
        },
    },
    methods: {
        normalizeSize,
        /**
         * Add or update an error message for a specific key
         * @param {string} key - The error key
         * @param {string} message - The error message (empty string to remove)
         * @returns {Object} The updated errors object
         */
        putError(key, message) {
            if (message) {
                // Add or update the error
                this.$set(this.errors, key, message)
            } else {
                // If message is falsy, remove the error
                this.removeError(key)
            }
            return this.errors
        },

        /**
         * Remove an error by its key
         * @param {string} key - The error key to remove
         * @returns {Object} The updated errors object
         */
        removeError(key) {
            if (this.errors.hasOwnProperty(key)) {
                const newErrors = { ...this.errors }
                delete newErrors[key]
                this.errors = newErrors
            }
            return this.errors
        },

        /**
         * Check if there are any errors
         * @returns {boolean} True if there are no errors
         */
        isValid() {
            return Object.keys(this.errors).length === 0
        },

        updateDates() {
            this.validateRequiredDates()
            this.validateDateRange()
            this.validateTimeRange()
        },

        // Add this new method to handle date input changes
        handleTitleInput() {
            if (this.exceptionData.title && this.exceptionData.title.trim()) {
                this.removeError('title')
            } else {
                this.putError('title', this.$t('validation.title_required'))
            }
        },

        handleDateInput(field) {
            // Clear any previous errors for this field
            this.removeError(field)

            // If we're updating the start date and it's after the end date, update the end date
            if (
                field === 'start' &&
                this.exceptionData.end &&
                new Date(this.exceptionData.start) > new Date(this.exceptionData.end)
            ) {
                this.exceptionData.end = this.exceptionData.start
            }

            // Update the end date if needed and validate
            this.updateEndDate()
            this.updateDates()
        },

        // Add this new method to handle time input changes
        handleTimeInput() {
            // Clear any previous time range errors
            this.removeError('timeRange')

            // Only validate if we're in timeslot mode and have both times
            if (this.isTimeSlot && this.exceptionData.startTime && this.exceptionData.endTime) {
                this.validateTimeRange()
            }
        },

        validateRequiredDates() {
            let hasErrors = false

            // Check start date
            if (!this.exceptionData.start) {
                this.putError('start', this.$t('validation.start_date_required'))
                hasErrors = true
            } else {
                this.removeError('start')
            }

            // Check end date
            if (!this.exceptionData.end) {
                this.putError('end', this.$t('validation.end_date_required'))
                hasErrors = true
            } else {
                this.removeError('end')
            }

            return !hasErrors
        },

        validateDateRange() {
            this.removeError('dateRange')

            if (!this.exceptionData.startDate || !this.exceptionData.endDate) {
                return false
            }

            const start = new Date(this.exceptionData.startDate + ' ' + this.exceptionData.startTime)
            const end = new Date(this.exceptionData.endDate + ' ' + this.exceptionData.endTime)

            if (start > end) {
                this.putError('dateRange', this.$t('validation.start_date_before_end_date'))
                return false
            } else {
                this.removeError('dateRange')
                return true
            }
        },

        validateTimeRange() {
            // Clear any previous time range errors
            this.removeError('timeRange')

            // If not in timeslot mode, no need to validate time range
            if (!this.isTimeSlot) {
                return true
            }

            // Check if we have both times
            if (!this.exceptionData.startTime || !this.exceptionData.endTime) {
                this.putError('timeRange', this.$t('validation.time_required'))
                return false
            }

            // Only validate time order if it's the same date
            if (this.isSameDate) {
                const [startHours, startMinutes] = this.exceptionData.startTime.split(':').map(Number)
                const [endHours, endMinutes] = this.exceptionData.endTime.split(':').map(Number)

                if (startHours > endHours || (startHours === endHours && startMinutes >= endMinutes)) {
                    this.putError('timeRange', this.$t('validation.start_time_before_end_time'))
                    return false
                }
            }

            // If we get here, validation passed
            this.removeError('timeRange')
            return true
        },

        handleTimeSlot(value) {
            this.isTimeSlot = value
            this.removeError('timeRange')

            if (!value) {
                this.exceptionData.startTime = '00:00'
                this.exceptionData.endTime = '23:59'
                // Clear time range errors when switching to all-day mode
                this.removeError('timeRange')
            } else {
                this.exceptionData.startTime = '09:00'
                this.exceptionData.endTime = '17:00'
                // Re-validate time range when switching to time slot mode
                this.validateTimeRange()
            }
        },
        selectColor(color) {
            this.selectedColor = color
            this.exceptionData.color = color.hex
            this.showColorPicker = false
        },
        selectReason(reason) {
            this.exceptionData.reason = reason
            this.showReasonDropdown = false
            this.removeError('reason')
        },
        initializeWithSelectedException() {
            if (
                this.formatTimeForInput(this.selectedException.start) === '00:00' &&
                this.formatTimeForInput(this.selectedException.end) === '23:59'
            ) {
                this.isTimeSlot = false
            }
            const foundReason = this.reasonOptions.find((r) => r.id === this.selectedException.status) || null
            this.exceptionData = {
                id: this.selectedException.id,
                establishment_id: this.selectedException.establishment_id || 1,
                start: this.formatDateForInput(this.selectedException.start),
                end: this.formatDateForInput(this.selectedException.end),
                reason: foundReason ? { ...foundReason, title: this.$t(foundReason.titleKey) } : null,
                startTime: this.formatTimeForInput(this.selectedException.start),
                endTime: this.formatTimeForInput(this.selectedException.end),
                color: this.selectedException.color,
                title: this.selectedException.title,
            }
        },
        formatDateForInput(date) {
            if (!date) return ''
            const d = new Date(date)
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        },

        formatTimeForInput(date) {
            if (!date) return ''
            const d = new Date(date)
            const hour = String(d.getHours()).padStart(2, '0')
            const minute = String(d.getMinutes()).padStart(2, '0')
            return `${hour}:${minute}`
        },
        formatDisplayDate(dateString) {
            if (!dateString) return 'N/A'
            const options = { year: 'numeric', month: 'short', day: 'numeric' }
            return new Date(dateString).toLocaleDateString(this.$i18n?.locale || 'fr-FR', options)
        },
        updateEndDate() {
            // If end date is before start date, update it to match start date
            if (this.exceptionData.end < this.exceptionData.start) {
                this.exceptionData.end = this.exceptionData.start
            }
        },
        getStatusTitle(status) {
            return EXCEPTION_STATUS[status]?.title || 'Inconnu'
        },
        getStatusColor(status) {
            return EXCEPTION_STATUS[status]?.color || '#6B7280'
        },
        validateForm() {
            // Clear previous errors
            this.errors = {}

            // Validate required fields
            if (!this.exceptionData.title) {
                this.putError('title', this.$t('validation.title_required'))
            }

            // Validate dates
            this.validateRequiredDates()
            this.validateDateRange()

            // Validate time range if in timeslot mode
            if (this.isTimeSlot) {
                this.validateTimeRange()
            }

            // Validate reason (motif)
            if (!this.exceptionData.reason) {
                this.putError('reason', this.$t('validation.reason_required'))
            } else {
                this.removeError('reason')
            }

            return this.isValid()
        },

        saveException() {
            if (!this.validateForm()) {
                return
            }

            // Emit the save event with the exception data
            this.$emit('save', {
                ...this.exceptionData,
                id: this.mode === 'edit' ? this.exceptionData.id : undefined,
            })
        },
    },
}
</script>

<style scoped>
.ppv2-bg-\[\#36A3D2\] {
    background-color: #36a3d2;
}

.ppv2-text-\[\#021832\] {
    color: #021832;
}

.ppv2-border-\[\#021832\] {
    border-color: #021832;
}

.ppv2-bg-\[\#F44336\] {
    background-color: #f44336;
}

.ppv2-bg-\[\#4CAF50\] {
    background-color: #4caf50;
}

.ppv2-bg-\[\#9C27B0\] {
    background-color: #9c27b0;
}

.ppv2-bg-\[\#FF6B35\] {
    background-color: #ff6b35;
}

.ppv2-bg-\[\#2196F3\] {
    background-color: #2196f3;
}

.ppv2-bg-\[\#FF9800\] {
    background-color: #ff9800;
}

.ppv2-bg-\[\#E91E63\] {
    background-color: #e91e63;
}

.ppv2-bg-\[\#00BCD4\] {
    background-color: #00bcd4;
}

.ppv2-bg-\[\#FFEB3B\] {
    background-color: #ffeb3b;
}

.ppv2-bg-\[\#9E9E9E\] {
    background-color: #9e9e9e;
}

.ppv2-bg-\[\#1976D2\] {
    background-color: #1976d2;
}
.exception-range-planning {
    max-width: 800px;
    margin: 0 auto;
}

.status-badge-large {
    @apply ppv2-px-4 ppv2-py-2 ppv2-rounded-full ppv2-text-sm ppv2-text-white ppv2-min-w-[120px] ppv2-text-center;
}

.status-quick-button {
    @apply ppv2-px-4 ppv2-py-2 ppv2-border-2 ppv2-rounded-lg ppv2-text-sm ppv2-transition-all ppv2-duration-200 hover:ppv2-shadow-md;
}

.status-quick-button-active {
    @apply ppv2-shadow-lg;
}

/* Custom scrollbar */
::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}

/* Focus styles */
input:focus,
select:focus,
button:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
}

/* Custom select styling */
select {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
    padding-right: 2.5rem;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .ppv2-grid-cols-2 {
        grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    .ppv2-grid-cols-3 {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
